import type { Session } from '@supabase/supabase-js'
import {
  setRepository,
  setLedgerRepository,
  setRecurringExpenseRepository,
  setRecoveryAnchorRepository,
  LocalStorageRepo,
  LocalStorageLedgerRepo,
  LocalStorageRecurringExpenseRepo,
  LocalStorageRecoveryAnchorRepo,
} from '@/repository'
import { getSupabaseClient } from './client'
import { SupabaseDebtRepo } from './supabaseDebtRepo'
import { SupabaseLedgerRepo } from './supabaseLedgerRepo'
import { SupabaseRecurringExpenseRepo } from './supabaseRecurringExpenseRepo'
import { SupabaseRecoveryAnchorRepo } from './supabaseRecoveryAnchorRepo'
import { useDebtStore } from '@/store/debtStore'
import { useLedgerStore } from '@/store/ledgerStore'
import { useRecurringExpenseStore } from '@/store/recurringExpenseStore'
import { useAuthStore } from '@/auth/authStore'
import { useRepoVersionStore } from '@/repository/repoVersionStore'
import { trackRepoWrites } from '@/store/writeTracker'

/**
 * Binds the four repository singletons to either local-storage or
 * Supabase implementations based on the current session. Called
 * reactively from `AuthProvider` on every auth state change.
 *
 * The function is intentionally idempotent — calling it twice with the
 * same session is a no-op for behavior. Stores get reset so a fresh
 * load from whichever backend is now in play happens on the next render.
 *
 * For cloud binding, the workspace id must be resolved before the
 * Supabase repos can be instantiated. We read it from `useAuthStore`
 * which `AuthProvider` populates immediately after sign-in. If the
 * workspace id isn't available yet (race window) we fall back to local
 * repos — the next auth-state change after workspace resolution will
 * re-bind correctly.
 */
export async function bindReposToSession(session: Session | null): Promise<void> {
  if (!session) {
    bindLocalRepos()
    return
  }

  const client = getSupabaseClient()
  if (!client) {
    // Session somehow exists without a configured client — treat as
    // local-only (this should never happen in practice).
    bindLocalRepos()
    return
  }

  const userId = session.user.id
  const workspaceId = useAuthStore.getState().currentWorkspaceId

  if (!workspaceId) {
    // Workspace not resolved yet. AuthProvider will call us again once
    // it's loaded. Stay on local repos so stale-state isn't surfaced
    // from a half-bound cloud repo.
    return
  }

  // v0.8.56 — writes are counted so refresh-on-focus never lands on top
  // of an in-flight save (see writeTracker.ts).
  setRepository(trackRepoWrites(new SupabaseDebtRepo(client, userId, workspaceId)))
  setLedgerRepository(trackRepoWrites(new SupabaseLedgerRepo(client, userId, workspaceId)))
  setRecurringExpenseRepository(new SupabaseRecurringExpenseRepo(client, userId, workspaceId))
  setRecoveryAnchorRepository(new SupabaseRecoveryAnchorRepo(client, userId, workspaceId))

  resetStores()
}

function bindLocalRepos(): void {
  setRepository(new LocalStorageRepo())
  setLedgerRepository(new LocalStorageLedgerRepo())
  setRecurringExpenseRepository(new LocalStorageRecurringExpenseRepo())
  setRecoveryAnchorRepository(new LocalStorageRecoveryAnchorRepo())
  resetStores()
}

/**
 * Force-resets in-memory store state so the next render re-initialises
 * from whichever repo is now bound. Without this, a signed-in user's
 * Supabase data would still be hidden behind cached local-storage debts.
 *
 * v0.8.24 also bumps `repoVersion` so non-store-backed consumers
 * (`useRecoveryAnchor`) can subscribe and refetch on binding flip. Pre-fix
 * the Dashboard's Recovery Progress headline rendered with the local
 * anchor for an entire session after the user signed in to cloud.
 */
function resetStores(): void {
  useDebtStore.getState().reset?.()
  useLedgerStore.getState().reset?.()
  useRecurringExpenseStore.getState().reset?.()
  useRepoVersionStore.getState().bumpRepoVersion()
}
